import { type PhaseChangeLine, PhaseChangeType } from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import { DateTime, uuid } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { FormSection } from '../../../../components/forms/FormSection';
import { HookedSubmitButton } from '../../../../components/forms/V3HookedSubmitButton';
import type { UseTimeseriesChartReturn } from '../../../participantData/hooks/useTimeseriesChart';
import { COLORS } from '../../../participantData/utils';
import { PhaseChangeLineForm } from './PhaseChangeLineForm';

type FormValues = PhaseChangeLine;

interface PhaseChangeLineAddFormProps {
  chart: UseTimeseriesChartReturn;
  onSubmit: () => void;
}

const PhaseChangeLineAddForm = (props: PhaseChangeLineAddFormProps) => {
  const methods = useForm({
    defaultValues: {
      id: uuid(),
      name: '',
      timestamp: DateTime.now(),
      color: COLORS.red,
      type: PhaseChangeType.Minor,
    },
  });

  const onSubmit = (values: FormValues) => {
    props.chart.onAddPhaseChangeLine(values);
    props.onSubmit();
  };

  return (
    <ChakraV3.Stack
      bg='white'
      gap={4}
      w='md'
      layerStyle='outline.subtle'
      shadow='2xl'
      rounded='md'
      overflow='hidden'
      p={4}
    >
      <FormProvider {...methods}>
        <FormSection
          as='form'
          heading='Add phase change line'
          description='Add a new phase change line to the view'
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <ChakraV3.Separator />
          <PhaseChangeLineForm />
          <HookedSubmitButton w='full' size='sm'>
            Save
          </HookedSubmitButton>
        </FormSection>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props {
  chart: UseTimeseriesChartReturn;
}

export const PhaseChangeLineAddControl = (props: Props) => {
  const { chart } = props;

  return (
    <FloatingPopover
      placement='right-end'
      render={({ context }) => (
        <PhaseChangeLineAddForm chart={chart} onSubmit={() => context.onOpenChange(false)} />
      )}
    >
      <ChakraV3.Button w='full' variant='outline' size='sm'>
        <FaPlus />
        New phase change line
      </ChakraV3.Button>
    </FloatingPopover>
  );
};
